import React, { useRef, useState } from "react";
import { Camera, Loader2, X, Check } from "lucide-react";
import api from "@/lib/api";
import { isNativePlatform } from "@/lib/native";
import { toast } from "sonner";

// Reads a Blob as a base64 string (no data: prefix) for the native upload path.
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => {
      const s = String(r.result || "");
      resolve(s.includes(",") ? s.split(",")[1] : s);
    };
    r.onerror = () => reject(new Error("read-failed"));
    r.readAsDataURL(blob);
  });
}

// Uploads a (compressed) image and returns { file_id, url }. On the native app CapacitorHttp
// cannot reliably send multipart FormData, so we post base64 JSON to /upload/native; on the web
// we keep the standard multipart upload. Both hit the same hardened, authenticated backend.
export async function uploadImage(fileOrBlob) {
  if (isNativePlatform()) {
    const b64 = await blobToBase64(fileOrBlob);
    const { data } = await api.post("/upload/native", {
      filename: fileOrBlob.name || "photo.jpg",
      data: b64,
    });
    return data;
  }
  const fd = new FormData();
  fd.append("file", fileOrBlob);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

// Compresses an image client-side then uploads. Returns { file_id, url }.
export async function compress(file, { max = 1400, quality = 0.75 } = {}) {
  if (!file.type.startsWith("image/")) return file;
  return new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    img.onload = () => {
      let { width, height } = img;
      if (width > max || height > max) {
        const r = Math.min(max / width, max / height);
        width = Math.round(width * r); height = Math.round(height * r);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => resolve(new File([blob], "photo.jpg", { type: "image/jpeg" })), "image/jpeg", quality);
    };
    reader.readAsDataURL(file);
  });
}

export default function PhotoUpload({ value, onChange, label = "Take Photo", testid = "photo-upload", required = false }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const compressed = await compress(file);
      const data = await uploadImage(compressed);
      setPreview(URL.createObjectURL(compressed));
      onChange?.(data.file_id);
      toast.success("Photo attached");
    } catch (err) {
      const msg = err?.normalized?.message || "Couldn't upload the photo. Please check your connection and try again.";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid={testid}>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} data-testid={`${testid}-input`} />
      {preview || value ? (
        <div className="relative rounded-xl overflow-hidden border border-slate-200 w-full h-40 bg-slate-100">
          {preview && <img src={preview} alt="evidence" className="w-full h-full object-cover" />}
          {!preview && value && <div className="w-full h-full flex items-center justify-center text-brand"><Check size={30} /><span className="ml-2 text-sm font-medium">Photo attached</span></div>}
          <button type="button" onClick={() => { setPreview(null); onChange?.(null); }} data-testid={`${testid}-remove`}
            className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 shadow"><X size={16} /></button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`${testid}-trigger`}
          className={`w-full h-28 rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 transition-colors ${required ? "border-red-300 bg-red-50/40" : "border-slate-300 bg-slate-50"} hover:border-brand`}>
          {uploading ? <Loader2 className="animate-spin text-brand" /> : <Camera className="text-slate-400" size={26} />}
          <span className="text-sm font-medium text-slate-500">{uploading ? "Uploading…" : label}</span>
          {required && <span className="text-[11px] text-red-500 font-medium">Photo required</span>}
        </button>
      )}
    </div>
  );
}
